/**
 * API client for the Operations → Checklists page.
 *
 * Backend: oh_operations Django app, alfred-suffixed endpoints under
 * /api/v1/oh_operations/checklist/. Every `apiEndpoints.X` reference lives
 * INSIDE a function body (TDZ-safety — mirrors lessonPlanner.js / centre.js).
 */
import { isEmpty } from 'lodash'

import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'
import { makeRequestUrl } from '../app/helpers'
import { get, patch, post } from '../request'

const fetchChecklistRuns = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.checklistRuns}/alfred`, queries)
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching checklist runs')
    throw error
  }
}

const fetchChecklistBoard = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.checklistBoard}/alfred`, { centre: centreUuid })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching checklist board')
    throw error
  }
}

const fetchChecklistHistory = async ({ centre, cadence, from, to } = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.checklistHistory}/alfred`, { centre, cadence, from, to })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching checklist history')
    throw error
  }
}

const getChecklistRunDetail = async (uuid) => {
  try {
    const response = await get(`${apiEndpoints.checklistRuns}/${uuid}/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching checklist run detail')
    throw error
  }
}

const submitChecklistRun = async (uuid, items) => {
  try {
    const response = await post(
      `${apiEndpoints.checklistRuns}/${uuid}/submit/alfred`,
      { items }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in submitting checklist run')
    throw error
  }
}

// HQ rules on a photo flag Gordon raised: 'confirm' (the run stops counting)
// or 'clear' (the flag is hidden, the run stands). Returns the updated item.
const reviewChecklistPhotoFlag = async (runItemUuid, action) => {
  try {
    const response = await post(
      `${apiEndpoints.checklistRunItems}/${runItemUuid}/photo-flag/alfred`,
      { action }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in reviewing checklist photo flag')
    throw error
  }
}

const fetchChecklistTemplates = async () => {
  try {
    const response = await get(`${apiEndpoints.checklistTemplates}/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching checklist templates')
    throw error
  }
}

// ---- HQ checkpoint editor (HQ › Centre Operations › Maintenance Checklist) ----

const fetchChecklistSetup = async () => {
  try {
    const response = await get(`${apiEndpoints.checklistSetup}/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching checklist setup')
    throw error
  }
}

const createChecklistItem = async (payload) => {
  try {
    const response = await post(`${apiEndpoints.checklistSetup}/items/alfred`, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating checklist item')
    throw error
  }
}

const updateChecklistItem = async (uuid, payload) => {
  try {
    const response = await patch(`${apiEndpoints.checklistSetup}/items/${uuid}/alfred`, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating checklist item')
    throw error
  }
}

const reorderChecklistItems = async (templateUuid, itemUuids) => {
  try {
    const response = await post(`${apiEndpoints.checklistSetup}/reorder/alfred`, {
      template: templateUuid,
      items: itemUuids
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in reordering checklist items')
    throw error
  }
}

export {
  fetchChecklistRuns,
  fetchChecklistBoard,
  fetchChecklistHistory,
  getChecklistRunDetail,
  submitChecklistRun,
  reviewChecklistPhotoFlag,
  fetchChecklistTemplates,
  fetchChecklistSetup,
  createChecklistItem,
  updateChecklistItem,
  reorderChecklistItems
}
